import { useOptionalSpaceParameters } from '@src/components/AppLayout/SpaceParameters';
import { useEffect, useRef } from 'react';

export function useApplySpaceInfluence<TApi, TInfluence>({
  actOnInfluence,
  blotterTableApi,
  cleanupInfluence,
  influence,
  subscribeToInfluenceUpdates,
}: {
  actOnInfluence: (api: TApi, influence: TInfluence) => boolean | void;
  blotterTableApi: TApi | null | undefined;
  cleanupInfluence?: (api: TApi) => void;
  influence: TInfluence | undefined;
  subscribeToInfluenceUpdates?: (api: TApi, applyInfluence: () => boolean | void) => () => void;
}) {
  const context = useOptionalSpaceParameters();
  const active = context?.active === true;
  const hasAppliedInfluenceRef = useRef(false);

  useEffect(() => {
    if (blotterTableApi == null) {
      return;
    }

    if (!active || influence == null) {
      if (hasAppliedInfluenceRef.current) {
        cleanupInfluence?.(blotterTableApi);
        hasAppliedInfluenceRef.current = false;
      }
      return;
    }

    hasAppliedInfluenceRef.current = true;

    let unsubscribe: (() => void) | undefined;
    const applyInfluence = () => {
      const applied = actOnInfluence(blotterTableApi, influence);
      if (applied === true) {
        unsubscribe?.();
        unsubscribe = undefined;
      }
      return applied;
    };

    if (applyInfluence() === true) {
      return;
    }

    unsubscribe = subscribeToInfluenceUpdates?.(blotterTableApi, applyInfluence);

    return () => {
      unsubscribe?.();
    };
  }, [actOnInfluence, active, blotterTableApi, cleanupInfluence, influence, subscribeToInfluenceUpdates]);

  return { active, influence };
}
